import { PictureAsPdf } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Checkbox, FormControlLabel } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '~/api/helpers';
import { useGeneratePlayoutDetailPdf } from '~/api/playout-reports/generate-playout-detail-pdf';
import { GeneratePlayoutDetailPdfDocument } from '~/api/playout-reports/generate-playout-detail-pdf.generated';
import { Dialog, DialogForm, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { PlayoutReportPdfSection } from '~/generated/graphql';
import { usePlayoutReport } from '../tabs/context';
import { ReportDownloadDialog } from './download-dialog';

export type PdfExportIdentifier =
  | { asset: { assetId: number; kind: string } }
  | { sponsorId: number };

export interface PdfExportControlsProps {
  identifier: PdfExportIdentifier;
  name: string;
  eventCount: number;
  hasTableRows: boolean;
  hasThumbnail?: boolean;
}

const toIdArray = (value: ReadonlyArray<number> | number | null | undefined) =>
  value == null ? value : ([] as number[]).concat(value);

interface SectionCheckboxProps {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}

const SectionCheckbox = ({ label, checked, disabled = false, onToggle }: SectionCheckboxProps) => (
  <FormControlLabel
    control={<Checkbox checked={checked} onChange={onToggle} size="small" sx={{ py: 0.5 }} />}
    label={label}
    disabled={disabled}
    slotProps={{
      typography: {
        variant: 'body1',
        color: checked && !disabled ? 'text.primary' : 'text.secondary',
      },
    }}
    sx={{ '&&': { alignItems: 'center' } }}
  />
);

// Every group splits the shared form width 50/50 so the checkbox columns
// line up across sections, while the dialog still shrinks to the widest
// pair of labels.
const GroupRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ mt: 1 }}>
    <Box sx={{ fontSize: 16, mb: 0.5 }}>{label}</Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: 3 }}>
      {children}
    </Box>
  </Box>
);

export const PdfExportControls = ({
  identifier,
  name,
  eventCount,
  hasTableRows,
  hasThumbnail = false,
}: PdfExportControlsProps) => {
  const { t } = useTranslation();
  const { variables, querySkipped } = usePlayoutReport();

  const isAssetDetail = 'asset' in identifier;
  const hasEventCharts = eventCount > 2;
  const hasEventList = eventCount > 0;
  const showThumbnail = isAssetDetail && hasThumbnail;

  const [open, setOpen] = useState(false);
  const [disabledSections, setDisabledSections] = useState<Set<PlayoutReportPdfSection>>(
    () => new Set(),
  );
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const labels = useMemo(() => {
    const detailScope = isAssetDetail ? 'by_asset' : 'by_sponsor';
    return {
      [PlayoutReportPdfSection.TotalPlays]: t(
        'pages.playout_reports.pdf_export.sections.total_plays',
      ),
      [PlayoutReportPdfSection.TotalAirtime]: t(
        'pages.playout_reports.pdf_export.sections.total_airtime',
      ),
      [PlayoutReportPdfSection.EventsCount]: t(
        `pages.playout_reports.${detailScope}.detail.events_count.label`,
      ),
      [PlayoutReportPdfSection.DevicesReached]: t(
        'pages.playout_reports.stats.devices_reached.label',
      ),
      [PlayoutReportPdfSection.PlaysByEvent]: t(
        'pages.playout_reports.charts.by_event_plays.title',
      ),
      [PlayoutReportPdfSection.DurationByEvent]: t(
        'pages.playout_reports.charts.by_event_duration.title',
      ),
      [PlayoutReportPdfSection.EventBreakdown]: t('pages.playout_reports.by_event_list.title'),
      [PlayoutReportPdfSection.ByZone]: t('pages.playout_reports.charts.by_zone.title'),
      [PlayoutReportPdfSection.ByDeviceGroup]: t(
        'pages.playout_reports.charts.by_device_group.title',
      ),
      [PlayoutReportPdfSection.AssociatedSponsors]: t(
        'pages.playout_reports.by_asset.detail.associated_sponsors',
      ),
      [PlayoutReportPdfSection.AssetSummary]: t('pages.playout_reports.by_asset.title'),
      [PlayoutReportPdfSection.Thumbnail]: t('pages.playout_reports.by_asset.detail.thumbnail'),
    };
  }, [t, isAssetDetail]);

  const tableSection = isAssetDetail
    ? PlayoutReportPdfSection.AssociatedSponsors
    : PlayoutReportPdfSection.AssetSummary;

  const isEnabled = (section: PlayoutReportPdfSection) => !disabledSections.has(section);

  const toggle = (section: PlayoutReportPdfSection) =>
    setDisabledSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });

  const sections = useMemo(() => {
    const all: PlayoutReportPdfSection[] = [
      PlayoutReportPdfSection.TotalPlays,
      PlayoutReportPdfSection.TotalAirtime,
      PlayoutReportPdfSection.EventsCount,
      PlayoutReportPdfSection.DevicesReached,
      ...(hasEventCharts
        ? [PlayoutReportPdfSection.PlaysByEvent, PlayoutReportPdfSection.DurationByEvent]
        : []),
      ...(hasEventList ? [PlayoutReportPdfSection.EventBreakdown] : []),
      PlayoutReportPdfSection.ByZone,
      PlayoutReportPdfSection.ByDeviceGroup,
      ...(hasTableRows ? [tableSection] : []),
      ...(showThumbnail ? [PlayoutReportPdfSection.Thumbnail] : []),
    ];
    return all.filter((s) => !disabledSections.has(s));
  }, [hasEventCharts, hasEventList, hasTableRows, tableSection, showThumbnail, disabledSections]);

  const [generate, { loading }] = useGeneratePlayoutDetailPdf(
    useNotifications(GeneratePlayoutDetailPdfDocument, {
      failure: t('pages.playout_reports.pdf_export.error'),
    }),
  );

  const handleExport = async () => {
    if (!variables.from || !variables.to) return;
    const { data } = await generate({
      variables: {
        input: {
          eventTypeId: variables.eventTypeId,
          from: variables.from,
          to: variables.to,
          deviceGroupIds: toIdArray(variables.deviceGroupIds),
          eventIds: toIdArray(variables.eventIds),
          combineByName: variables.combineByName,
          sections,
          identifier,
        },
      },
    });
    if (data?.generatePlayoutDetailPdf?.url) {
      setOpen(false);
      setDownloadUrl(data.generatePlayoutDetailPdf.url);
    }
  };

  const statSections = [
    PlayoutReportPdfSection.TotalPlays,
    PlayoutReportPdfSection.TotalAirtime,
    PlayoutReportPdfSection.EventsCount,
    PlayoutReportPdfSection.DevicesReached,
  ];
  const chartSections = [
    PlayoutReportPdfSection.PlaysByEvent,
    PlayoutReportPdfSection.DurationByEvent,
  ];
  const donutSections = [PlayoutReportPdfSection.ByZone, PlayoutReportPdfSection.ByDeviceGroup];

  return (
    <>
      <Button
        variant="contained"
        startIcon={<PictureAsPdf />}
        onClick={() => setOpen(true)}
        disabled={querySkipped || !variables.from || !variables.to}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {t('pages.playout_reports.pdf_export.button')}
      </Button>

      <Dialog maxWidth="sm" onClose={() => setOpen(false)} open={open}>
        <DialogTitle onClose={() => setOpen(false)}>
          {isAssetDetail
            ? t('pages.playout_reports.pdf_export.dialog_title_asset')
            : t('pages.playout_reports.pdf_export.dialog_title_sponsor')}
        </DialogTitle>
        <DialogContent>
          <DialogForm
            id="pdf-export"
            onSubmit={(e) => {
              e.preventDefault();
              void handleExport();
            }}
          >
            <GroupRow label={t('pages.playout_reports.pdf_export.groups.summary')}>
              {statSections.map((section) => (
                <SectionCheckbox
                  key={section}
                  label={labels[section]}
                  checked={isEnabled(section)}
                  onToggle={() => toggle(section)}
                />
              ))}
            </GroupRow>

            <GroupRow label={t('pages.playout_reports.pdf_export.groups.breakdowns')}>
              {chartSections.map((section) => (
                <SectionCheckbox
                  key={section}
                  label={labels[section]}
                  checked={hasEventCharts && isEnabled(section)}
                  disabled={!hasEventCharts}
                  onToggle={() => toggle(section)}
                />
              ))}
              <SectionCheckbox
                label={labels[PlayoutReportPdfSection.EventBreakdown]}
                checked={hasEventList && isEnabled(PlayoutReportPdfSection.EventBreakdown)}
                disabled={!hasEventList}
                onToggle={() => toggle(PlayoutReportPdfSection.EventBreakdown)}
              />
              {donutSections.map((section) => (
                <SectionCheckbox
                  key={section}
                  label={labels[section]}
                  checked={isEnabled(section)}
                  onToggle={() => toggle(section)}
                />
              ))}
            </GroupRow>

            {(hasTableRows || showThumbnail) && (
              <GroupRow label={t('pages.playout_reports.pdf_export.groups.detail')}>
                {hasTableRows && (
                  <SectionCheckbox
                    label={labels[tableSection]}
                    checked={isEnabled(tableSection)}
                    onToggle={() => toggle(tableSection)}
                  />
                )}
                {showThumbnail && (
                  <SectionCheckbox
                    label={labels[PlayoutReportPdfSection.Thumbnail]}
                    checked={isEnabled(PlayoutReportPdfSection.Thumbnail)}
                    onToggle={() => toggle(PlayoutReportPdfSection.Thumbnail)}
                  />
                )}
              </GroupRow>
            )}
          </DialogForm>
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={() => setOpen(false)} variant="outlined">
            {t('common.actions.cancel')}
          </Button>
          <LoadingButton
            color="primary"
            disabled={sections.length === 0}
            form="pdf-export"
            loading={loading}
            type="submit"
            variant="contained"
          >
            {t('common.buttons.export')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ReportDownloadDialog
        close={() => setDownloadUrl(null)}
        from={variables.from ? DateTime.fromISO(variables.from) : null}
        to={variables.to ? DateTime.fromISO(variables.to) : null}
        reportUrl={downloadUrl}
        title={
          isAssetDetail
            ? t('pages.playout_reports.pdf_export.download.title_asset')
            : t('pages.playout_reports.pdf_export.download.title_sponsor')
        }
        name={name}
        closeLabel={t('common.dialogs.close')}
      />
    </>
  );
};
